import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import type { StoreApi, UseBoundStore } from 'zustand';
import type { DrawerStore, DrawerStackEntry } from '@hero/hooks-shared';
import Spinner from '../Spinner/Spinner';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SHEET_CLOSE_ANIMATION_MS,
} from '../../ui/Sheet/Sheet';

export interface StackedDrawerProps<T> {
  /** Zustand store from `createDrawerStore()` (callable as a hook; has `getState()` for URL cleanup). */
  useStore: UseBoundStore<StoreApi<DrawerStore>>;
  useItemApi: (id: string | undefined) => { data: T | undefined; isPending: boolean };
  renderContent: (item: T, onClose: () => void) => React.ReactNode;
  syncUrl?: boolean | string;
  defaultParamKey?: string;
  sheetClassName?: string;
  title?: string;
  description?: string;
  notFoundLabel?: string;
}

interface SheetEntryProps<T> {
  entry: DrawerStackEntry;
  useItemApi: (id: string | undefined) => { data: T | undefined; isPending: boolean };
  renderContent: (item: T, onClose: () => void) => React.ReactNode;
  sheetClassName: string;
  title: string;
  description: string;
  notFoundLabel: string;
  onClose: () => void;
}

function DrawerSheetEntry<T>({
  entry,
  useItemApi,
  sheetClassName,
  title,
  description,
  notFoundLabel,
  renderContent,
  onClose,
}: SheetEntryProps<T>) {
  const [sheetOpen, setSheetOpen] = useState(true);
  const isClosingRef = useRef(false);

  const handleClose = useCallback(() => {
    if (isClosingRef.current) return;
    isClosingRef.current = true;
    setSheetOpen(false);
    setTimeout(onClose, SHEET_CLOSE_ANIMATION_MS);
  }, [onClose]);

  const { data, isPending } = useItemApi(entry.itemId);

  return (
    <Sheet open={sheetOpen} onOpenChange={nextOpen => !nextOpen && handleClose()}>
      <SheetContent side="right" className={sheetClassName}>
        <SheetHeader className="sr-only">
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription>{description}</SheetDescription>
        </SheetHeader>
        {isPending && !data && (
          <div className="flex items-center justify-center h-full">
            <Spinner />
          </div>
        )}
        {data && renderContent(data, handleClose)}
        {!isPending && !data && (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            {notFoundLabel}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

/**
 * Generic stacked drawer that manages:
 * - Stack-based rendering from a Zustand `DrawerStore`
 * - Optional URL search-param sync (`syncUrl` prop)
 * - Smooth close animations
 * - Automatic data fetching by ID (data lives in React Query cache, seeded by list queries)
 *
 * @example
 * ```tsx
 * <StackedDrawer
 *   useStore={useOrderDrawerStore}
 *   useItemApi={(id) => useOrderApi(id)}
 *   syncUrl="orderId"
 *   sheetClassName="flex flex-col p-8 bg-white"
 *   title="Order Details"
 *   description="View order information"
 *   renderContent={(order, onClose) => (
 *     <OrderDrawerContent order={order} onClose={onClose} />
 *   )}
 * />
 * ```
 */
export function StackedDrawer<T>({
  useStore,
  useItemApi,
  renderContent,
  syncUrl,
  defaultParamKey = 'id',
  sheetClassName = 'flex flex-col p-0 bg-white',
  title = 'Details',
  description = 'View details',
  notFoundLabel = 'No data found',
}: StackedDrawerProps<T>) {
  const { stack, open, close } = useStore();
  const [searchParams, setSearchParams] = useSearchParams();

  const paramKey = typeof syncUrl === 'string' ? syncUrl : defaultParamKey;
  const shouldSyncUrl = !!syncUrl;
  const closingRef = useRef(false);

  useEffect(() => {
    if (!shouldSyncUrl || closingRef.current) return;
    const urlId = searchParams.get(paramKey);
    if (!urlId || stack.length > 0) return;
    open(urlId);
  }, [searchParams, paramKey, shouldSyncUrl, stack.length, open]);

  useEffect(() => {
    if (!shouldSyncUrl) return;
    const top = stack[stack.length - 1];
    if (top?.itemId) {
      if (searchParams.get(paramKey) !== top.itemId) {
        setSearchParams(
          prev => {
            const next = new URLSearchParams(prev);
            next.set(paramKey, top.itemId);
            return next;
          },
          { replace: true },
        );
      }
    }
  }, [stack, shouldSyncUrl, paramKey, searchParams, setSearchParams]);

  // Keep `closingRef` true until the URL param is actually gone — otherwise the
  // open-from-URL effect can re-fire while `?id=` is still present and flicker.
  useEffect(() => {
    if (!shouldSyncUrl) return;
    if (!searchParams.get(paramKey)) {
      closingRef.current = false;
    }
  }, [searchParams, paramKey, shouldSyncUrl]);

  const handleClose = useCallback(() => {
    closingRef.current = true;

    if (shouldSyncUrl && useStore.getState().stack.length <= 1) {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.delete(paramKey);
          return next;
        },
        { replace: true },
      );
    }

    close();
  }, [close, shouldSyncUrl, paramKey, setSearchParams, useStore]);

  return (
    <>
      {stack.map(entry => (
        <DrawerSheetEntry<T>
          key={entry.key}
          entry={entry}
          useItemApi={useItemApi}
          sheetClassName={sheetClassName}
          title={title}
          description={description}
          notFoundLabel={notFoundLabel}
          renderContent={renderContent}
          onClose={handleClose}
        />
      ))}
    </>
  );
}
